import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export interface Step {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'completed' | 'failed' | 'waiting';
  startTime?: Date;
  endTime?: Date;
  estimatedDuration?: number; // in seconds
  errorMessage?: string;
  details?: string;
}

interface StepVisualizerProps {
  currentStep: string;
  steps: Step[];
  onRetry?: (stepId: string) => void;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}

const StepVisualizer: React.FC<StepVisualizerProps> = ({
  currentStep,
  steps,
  onRetry,
  className = '',
  orientation = 'vertical'
}) => {
  const [selectedStep, setSelectedStep] = useState<string | null>(null);
  const [timeElapsed, setTimeElapsed] = useState<Record<string, number>>({});

  // Update elapsed time for active steps
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeElapsed(prev => {
        const newElapsed = { ...prev };
        steps.forEach(step => {
          if (step.status === 'active' && step.startTime) {
            newElapsed[step.id] = Math.floor((Date.now() - step.startTime.getTime()) / 1000);
          }
        });
        return newElapsed;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [steps]);

  const getStepIndex = (stepId: string) => steps.findIndex(step => step.id === stepId);
  const currentStepIndex = getStepIndex(currentStep);
  const completedSteps = steps.filter(step => step.status === 'completed').length;
  const progressPercentage = (completedSteps / steps.length) * 100;

  const getStatusIcon = (step: Step) => {
    switch (step.status) {
      case 'completed':
        return (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </motion.div>
        );
      case 'failed':
        return (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.1 }}
            className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center cursor-pointer"
            onClick={() => onRetry && onRetry(step.id)}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </motion.div>
        );
      case 'active':
        return (
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              boxShadow: [
                '0 0 0 0 rgba(59, 130, 246, 0.7)',
                '0 0 0 10px rgba(59, 130, 246, 0)',
                '0 0 0 0 rgba(59, 130, 246, 0)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-3 h-3 border-2 border-white border-t-transparent rounded-full"
            />
          </motion.div>
        );
      case 'waiting':
        return (
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </motion.div>
        );
      default:
        return (
          <div className="w-6 h-6 bg-gray-600 rounded-full border-2 border-gray-500" />
        );
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getEstimatedTimeRemaining = () => {
    const remainingSteps = steps.slice(currentStepIndex + 1);
    const totalEstimated = remainingSteps.reduce((sum, step) => sum + (step.estimatedDuration || 0), 0);
    return totalEstimated;
  };

  const containerClasses = orientation === 'horizontal'
    ? 'flex flex-row items-center space-x-4 overflow-x-auto'
    : 'flex flex-col space-y-4';

  return (
    <div className={`bg-gray-900 rounded-lg p-6 ${className}`}>
      {/* Progress Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold text-white">Domain Verification Progress</h3>
          <span className="text-sm text-gray-400">
            {completedSteps}/{steps.length} steps completed
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
          <motion.div
            className="bg-blue-500 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>

        <div className="flex justify-between text-xs text-gray-400">
          <span>{Math.round(progressPercentage)}% complete</span>
          {getEstimatedTimeRemaining() > 0 && (
            <span>~{formatDuration(getEstimatedTimeRemaining())} remaining</span>
          )}
        </div>
      </div>

      {/* Steps Container */}
      <div className={containerClasses}>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            {/* Step Item */}
            <motion.div
              layout
              className={`relative ${orientation === 'horizontal' ? 'min-w-48' : 'w-full'}`}
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                className={`
                  border rounded-lg p-4 cursor-pointer transition-all duration-200
                  ${step.status === 'active' ? 'border-blue-500 bg-gray-800' :
                    step.status === 'completed' ? 'border-green-500 bg-gray-800' :
                    step.status === 'failed' ? 'border-red-500 bg-gray-800' :
                    step.status === 'waiting' ? 'border-yellow-500 bg-gray-800' :
                    'border-gray-600 bg-gray-800'}
                  ${selectedStep === step.id ? 'ring-2 ring-blue-400' : ''}
                `}
                onClick={() => setSelectedStep(selectedStep === step.id ? null : step.id)}
              >
                {/* Step Header */}
                <div className="flex items-center space-x-3 mb-2">
                  {getStatusIcon(step)}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-white truncate">
                      {step.title}
                    </h4>
                    <p className="text-xs text-gray-400 truncate">
                      {step.description}
                    </p>
                  </div>
                  {step.status === 'failed' && onRetry && (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRetry(step.id);
                      }}
                      className="text-xs bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded"
                    >
                      Retry
                    </motion.button>
                  )}
                </div>

                {/* Timing Info */}
                {(step.status === 'active' || step.status === 'completed' || step.status === 'failed') && (
                  <div className="text-xs text-gray-500">
                    {step.status === 'active' && timeElapsed[step.id] && (
                      <span>Running for {formatDuration(timeElapsed[step.id])}</span>
                    )}
                    {step.status === 'completed' && step.startTime && step.endTime && (
                      <span>
                        Completed in {formatDuration(Math.floor((step.endTime.getTime() - step.startTime.getTime()) / 1000))}
                      </span>
                    )}
                    {step.estimatedDuration && (step.status !== 'completed' && step.status !== 'active' && step.status !== 'failed') && (
                      <span>Est. {formatDuration(step.estimatedDuration)}</span>
                    )}
                  </div>
                )}

                {/* Expanded Details */}
                <AnimatePresence>
                  {selectedStep === step.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="mt-3 pt-3 border-t border-gray-600"
                    >
                      {step.details && (
                        <p className="text-sm text-gray-300 mb-2">{step.details}</p>
                      )}
                      {step.errorMessage && (
                        <p className="text-sm text-red-400">{step.errorMessage}</p>
                      )}
                      {step.startTime && (
                        <p className="text-xs text-gray-500">
                          Started: {step.startTime.toLocaleTimeString()}
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Active Step Animation Overlay */}
                {step.status === 'active' && (
                  <motion.div
                    className="absolute inset-0 border border-blue-400 rounded-lg pointer-events-none"
                    animate={{
                      opacity: [0.3, 0.7, 0.3],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                )}

                {/* Failed Step Shake Animation */}
                {step.status === 'failed' && (
                  <motion.div
                    className="absolute inset-0 pointer-events-none"
                    animate={{
                      x: [-2, 2, -2, 2, 0],
                    }}
                    transition={{
                      duration: 0.5,
                      repeat: 2,
                    }}
                  />
                )}
              </motion.div>
            </motion.div>

            {/* Connector Line */}
            {orientation === 'vertical' && index < steps.length - 1 && (
              <div className="flex justify-center">
                <motion.div
                  className={`w-0.5 h-4 ${
                    index < currentStepIndex ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: index * 0.1 }}
                />
              </div>
            )}

            {orientation === 'horizontal' && index < steps.length - 1 && (
              <div className="flex items-center">
                <motion.div
                  className={`h-0.5 w-8 ${
                    index < currentStepIndex ? 'bg-green-500' : 'bg-gray-600'
                  }`}
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: index * 0.1 }}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Success Animation */}
      <AnimatePresence>
        {completedSteps === steps.length && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-6 p-4 bg-green-900 border border-green-500 rounded-lg"
          >
            <div className="flex items-center space-x-3">
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 1 }}
                className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>
              <div>
                <h4 className="text-green-400 font-medium">Domain Verification Complete!</h4>
                <p className="text-green-300 text-sm">Your domain has been successfully verified and is ready to use.</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StepVisualizer;